'use client'

import { useCallback, useEffect, useState } from 'react'

import { useAuth } from '@/hooks/use-auth'

interface ShopifyStorefrontToolsAccess {
  allowed: boolean
  loading: boolean
}

/**
 * Hook for plan-gated visibility of Shopify Storefront MCP tools (Catalog
 * Search + Policies & FAQs) inside email/messaging agent settings.
 *
 * The result is constant for the lifetime of the session — `billing_channel`
 * is immutable per tenant by Shopify App Store policy. We still re-fetch on
 * mount per consumer for simplicity (the endpoint is a single PK lookup);
 * if the same hook is used by N components on the same page each one will
 * issue its own GET. That's fine in practice but trivially de-dupable later
 * via SWR if it ever shows up in profiling.
 */
export function useShopifyStorefrontToolsAccess(): ShopifyStorefrontToolsAccess {
  const { fetchWithAuth } = useAuth()
  const [state, setState] = useState<ShopifyStorefrontToolsAccess>({
    allowed: false,
    loading: true,
  })

  const check = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/billing/shopify-storefront-tools-access')
      if (!res.ok) {
        setState({ allowed: false, loading: false })
        return
      }
      const data = (await res.json()) as { allowed?: boolean }
      setState({ allowed: Boolean(data.allowed), loading: false })
    } catch {
      setState({ allowed: false, loading: false })
    }
  }, [fetchWithAuth])

  useEffect(() => {
    check()
  }, [check])

  return state
}
